import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../../redux/store";
import { listenToShortstayData, upsertShortstay } from "../../../../redux/shortstaysSlice";
import RoomIcon from "@mui/icons-material/Room";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { TextField, Button, Box, Autocomplete } from "@mui/material";
import countries from "../../../../GLOBAL/Countries";

declare global {
  interface Window {
    google: any;
  }
}

const T = {
  bg:       "#FFFFFF",
  text:     "#0A0A0A",
  textSoft: "#444444",
  muted:    "#9A9A9A",
  border:   "#EBEBEB",
  gold:     "#C9A84C",
  goldDark: "#9A7A2E",
  black:    "#0A0A0A",
  sans:     "'Montserrat', sans-serif",
};

/* Shared MUI input styling to match the flat, white, no-3d look */
const muiFieldSx = (invalid = false) => ({
  mb: 2,
  "& .MuiInputBase-input": {
    color: T.black,
    fontFamily: T.sans,
    fontSize: 13,
  },
  "& .MuiInputLabel-root": {
    color: T.textSoft,
    fontFamily: T.sans,
    fontSize: 12,
  },
  "& .MuiInputLabel-root.Mui-focused": {
    color: T.black,
  },
  "& .MuiOutlinedInput-root": {
    backgroundColor: "#FAFAFA",
    borderRadius: "10px",
    boxShadow: "none",
    "& fieldset": {
      borderColor: invalid ? "#E24B4A" : T.border,
      borderWidth: 1,
    },
    "&:hover fieldset": {
      borderColor: invalid ? "#E24B4A" : T.black,
    },
    "&.Mui-focused fieldset": {
      borderColor: invalid ? "#E24B4A" : T.black,
      borderWidth: 1,
    },
  },
});

const ShortAddress: React.FC = () => {
  const { listingId } = useParams<{ listingId: string }>();
  const dispatch = useDispatch<AppDispatch>();
  const currentListing = useSelector((state: RootState) => state.shortstays.currentListing);

  const [isSaving, setIsSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [address, setAddress] = useState({
    country: "",
    street: "",
    farmName: "",
    neighborhood: "",
    apt: "",
    zip: "",
    city: "",
    county: "",
    latitude: null as number | null,
    longitude: null as number | null,
  });

  const streetInputRef = useRef<HTMLInputElement>(null);

  // Fetch & populate existing data
  useEffect(() => {
    if (listingId) {
      const unsub = dispatch(listenToShortstayData(listingId));
      return () => {
        if (typeof unsub === "function") unsub();
      };
    }
  }, [listingId, dispatch]);

  useEffect(() => {
    const addr = currentListing?.address;
    if (addr) {
      setAddress((prev) => ({
        ...prev,
        ...addr,
        latitude:
          typeof addr.latitude === "number"
            ? addr.latitude
            : Number(addr.latitude) || null,
        longitude:
          typeof addr.longitude === "number"
            ? addr.longitude
            : Number(addr.longitude) || null,
      }));
    }
  }, [currentListing]);

  // Google Autocomplete
  useEffect(() => {
    if (!window.google?.maps?.places || !streetInputRef.current) return;
    const autocomplete = new window.google.maps.places.Autocomplete(streetInputRef.current, {
      types: ["address"],
    });
    autocomplete.addListener("place_changed", () => {
      const place = autocomplete.getPlace();
      const components = place.address_components || [];
      const getComponent = (type: string) =>
        components.find((c: any) => c.types.includes(type))?.long_name || "";

      const streetNumber = getComponent("street_number");
      const route = getComponent("route");
      const city = getComponent("locality") || getComponent("administrative_area_level_2");
      const neighborhood = getComponent("sublocality") || "";
      const country = getComponent("country");
      const county = getComponent("administrative_area_level_1");
      const zip = getComponent("postal_code");
      const lat = place.geometry?.location?.lat() ?? null;
      const lng = place.geometry?.location?.lng() ?? null;

      const streetOnly = [streetNumber, route].filter(Boolean).join(" ");
      setAddress((prev) => ({
        ...prev,
        street: streetOnly || prev.street,
        city,
        neighborhood,
        country,
        county,
        zip,
        latitude: lat,
        longitude: lng,
      }));
    });
  }, []);

  // Use current location
  const handleUseCurrentLocation = () => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const lat = pos.coords.latitude;
        const lng = pos.coords.longitude;
        const geocoder = new window.google.maps.Geocoder();
        geocoder.geocode({ location: { lat, lng } }, (results: any, status: any) => {
          if (status === "OK" && results?.[0]) {
            const components = results[0].address_components || [];
            const getComponent = (type: string) =>
              components.find((c: any) => c.types.includes(type))?.long_name || "";
            const streetNumber = getComponent("street_number");
            const route = getComponent("route");
            const city = getComponent("locality") || getComponent("administrative_area_level_2");
            const neighborhood = getComponent("sublocality") || "";
            const country = getComponent("country");
            const county = getComponent("administrative_area_level_1");
            const zip = getComponent("postal_code");
            const streetOnly = [streetNumber, route].filter(Boolean).join(" ");

            setAddress((prev) => ({
              ...prev,
              street: streetOnly || prev.street,
              city,
              neighborhood,
              country,
              county,
              zip,
              latitude: lat,
              longitude: lng,
            }));
          }
        });
      },
      (err) => {
        console.error(err);
      }
    );
  };

  const handleChange = (field: string, value: string) => {
    if (field === "latitude" || field === "longitude") {
      const num = value ? parseFloat(value) : null;
      setAddress((prev) => ({ ...prev, [field]: num }));
    } else {
      setAddress((prev) => ({ ...prev, [field]: value }));
    }
  };

  const handleSave = async () => {
    if (!listingId) return;
    setIsSaving(true);
    setError(null);
    try {
      await dispatch(
        upsertShortstay({
          listingId,
          data: {
            address: {
              ...address,
              latitude: address.latitude ?? undefined,
              longitude: address.longitude ?? undefined,
            },
          },
        })
      );
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      console.error(err);
      setError("Failed to save. Please try again.");
      setTimeout(() => setError(null), 5000);
    } finally {
      setIsSaving(false);
    }
  };

  const requiredFields = ["street", "apt", "city", "county"];
  const isFieldInvalid = (field: string) =>
    !(address as any)[field]?.toString().trim();
  const isSaveDisabled =
    requiredFields.some(isFieldInvalid) ||
    address.latitude === null ||
    address.longitude === null;

  const fields = [
    { key: "farmName", label: "Farm name", optional: true },
    { key: "apt", label: "Apt name / Door number", optional: false },
    { key: "neighborhood", label: "Neighborhood", optional: true },
    { key: "zip", label: "ZIP / Postal code", optional: true },
    { key: "city", label: "City", optional: false },
    { key: "county", label: "County / State", optional: false },
  ];

  return (
    <Box sx={{ background: T.bg, color: T.text, fontFamily: T.sans, maxWidth: 560, mx: "auto", position: "relative" }}>

      {/* SAVING OVERLAY */}
      {isSaving && (
        <Box
          sx={{
            position: "fixed",
            inset: 0,
            background: "rgba(255,255,255,0.65)",
            backdropFilter: "blur(6px)",
            zIndex: 1100,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: "20px",
          }}
        >
          <div
            style={{
              width: 56,
              height: 56,
              borderRadius: "50%",
              border: `3px solid ${T.border}`,
              borderTop: `3px solid ${T.black}`,
              animation: "spin 0.8s linear infinite",
            }}
          />
          <p style={{ fontSize: 12, fontWeight: 500, color: T.black, letterSpacing: "0.08em", textTransform: "uppercase", margin: 0 }}>
            Saving
          </p>
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </Box>
      )}

      {/* SUCCESS TOAST */}
      {saved && (
        <Box
          sx={{
            position: "fixed",
            bottom: 32,
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 1200,
            background: "#FFFFFF",
            border: "1px solid #D4EDDA",
            borderRadius: "12px",
            padding: "14px 22px",
            display: "flex",
            alignItems: "center",
            gap: "10px",
            boxShadow: "0 8px 30px rgba(0,0,0,0.12)",
            minWidth: 220,
          }}
        >
          <CheckCircleOutlineIcon sx={{ color: "#22883F" }} />
          <div>
            <p style={{ fontSize: 12, fontWeight: 600, color: "#22883F", margin: 0 }}>Successfully saved</p>
            <p style={{ fontSize: 11, color: T.muted, margin: "2px 0 0" }}>Address updated</p>
          </div>
        </Box>
      )}

      {/* ERROR TOAST */}
      {error && (
        <Box
          sx={{
            position: "fixed",
            bottom: 32,
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 1200,
            background: "#FFFFFF",
            border: "1px solid #F7C1C1",
            borderRadius: "12px",
            padding: "14px 22px",
            display: "flex",
            alignItems: "center",
            gap: "10px",
            boxShadow: "0 8px 30px rgba(0,0,0,0.12)",
            minWidth: 220,
          }}
        >
          <ErrorOutlineIcon sx={{ color: "#E24B4A" }} />
          <div>
            <p style={{ fontSize: 12, fontWeight: 600, color: "#E24B4A", margin: 0 }}>Save failed</p>
            <p style={{ fontSize: 11, color: T.muted, margin: "2px 0 0" }}>{error}</p>
          </div>
        </Box>
      )}

      {/* heading */}
      <Box sx={{ mb: "28px" }}>
        <p style={{ fontSize: 9, color: T.muted, letterSpacing: "0.13em", textTransform: "uppercase", margin: "0 0 6px" }}>
          Property
        </p>
        <h2 style={{ fontSize: 20, fontWeight: 500, color: T.black, margin: 0 }}>Address</h2>
        <p style={{ fontSize: 12, color: T.muted, marginTop: 6 }}>
          We'll only share your address with guests after booking.
        </p>
      </Box>

      {/* FORM FIELDS — flat, no card wrap, MUI inputs */}
      <Box sx={{ display: "flex", flexDirection: "column" }}>

        {/* Street */}
        <TextField
          label="Street address *"
          value={address.street}
          onChange={(e) => handleChange("street", e.target.value)}
          inputRef={streetInputRef}
          placeholder="e.g. 12 Ocean Drive"
          fullWidth
          sx={muiFieldSx(isFieldInvalid("street"))}
          InputLabelProps={{ shrink: true }}
        />

        {/* Country */}
        <Autocomplete
          freeSolo
          disableClearable
          options={countries.map((c: any) => c.name)}
          value={address.country || ""}
          onChange={(_, newValue) => handleChange("country", newValue || "")}
          onInputChange={(_, newInput) => handleChange("country", newInput || "")}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Country / Region *"
              placeholder="Search country..."
              fullWidth
              sx={muiFieldSx(isFieldInvalid("country"))}
              InputLabelProps={{ shrink: true }}
            />
          )}
        />

        {/* Other fields */}
        {fields.map(({ key, label, optional }) => (
          <TextField
            key={key}
            label={label + (optional ? " (optional)" : " *")}
            value={(address as any)[key] || ""}
            onChange={(e) => handleChange(key, e.target.value)}
            fullWidth
            sx={muiFieldSx(!optional && isFieldInvalid(key))}
            InputLabelProps={{ shrink: true }}
          />
        ))}

        {/* Use current location */}
        <Button
          onClick={handleUseCurrentLocation}
          startIcon={<RoomIcon sx={{ fontSize: 16 }} />}
          sx={{
            alignSelf: "flex-start",
            background: "transparent",
            border: `1px solid ${T.border}`,
            borderRadius: "10px",
            padding: "9px 16px",
            fontSize: 12,
            fontWeight: 500,
            fontFamily: T.sans,
            color: T.black,
            textTransform: "none",
            boxShadow: "none",
            mt: 1,
            "&:hover": {
              borderColor: T.black,
              background: "#F5F5F5",
              boxShadow: "none",
            },
          }}
        >
          Use current location
        </Button>

        {/* coords read-only */}
        {(address.latitude !== null || address.longitude !== null) && (
          <Box
            sx={{
              display: "flex",
              gap: "10px",
              padding: "10px 14px",
              background: "#FAFAFA",
              border: `1px solid ${T.border}`,
              borderRadius: "10px",
              mt: 2,
            }}
          >
            <div style={{ flex: 1 }}>
              <p style={{ fontSize: 9, color: T.muted, letterSpacing: "0.1em", textTransform: "uppercase", margin: "0 0 2px" }}>Latitude</p>
              <p style={{ fontSize: 13, color: T.black, margin: 0, fontWeight: 500 }}>{address.latitude?.toFixed(6)}</p>
            </div>
            <div style={{ width: 1, background: T.border }} />
            <div style={{ flex: 1 }}>
              <p style={{ fontSize: 9, color: T.muted, letterSpacing: "0.1em", textTransform: "uppercase", margin: "0 0 2px" }}>Longitude</p>
              <p style={{ fontSize: 13, color: T.black, margin: 0, fontWeight: 500 }}>{address.longitude?.toFixed(6)}</p>
            </div>
          </Box>
        )}
      </Box>

      {/* save */}
      <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
        <Button
          onClick={handleSave}
          disabled={isSaveDisabled || isSaving}
          sx={{
            background: isSaveDisabled ? "#F0F0F0" : T.black,
            color: isSaveDisabled ? T.muted : "#FFFFFF",
            border: "none",
            borderRadius: "10px",
            padding: "11px 28px",
            fontSize: 12,
            fontWeight: 600,
            fontFamily: T.sans,
            textTransform: "none",
            letterSpacing: "0.04em",
            boxShadow: isSaveDisabled ? "none" : "0 4px 14px rgba(0,0,0,0.18)",
            "&:hover": {
              background: isSaveDisabled ? "#F0F0F0" : T.black,
              boxShadow: isSaveDisabled ? "none" : "0 4px 14px rgba(0,0,0,0.18)",
            },
          }}
        >
          Save changes
        </Button>
      </Box>
    </Box>
  );
};

export default ShortAddress;